import React, { useState, useEffect } from 'react'
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext'

const SupabaseRegisterForm = ({ onSwitchToLogin, onBackToGame }) => {
  const { signUp, checkUsernameAvailability } = useSupabaseAuth()
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, message: '' })

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  // Check username availability when username changes
  useEffect(() => {
    const checkUsername = async () => {
      if (formData.username.length < 3) {
        setUsernameStatus({ checking: false, available: null, message: '' })
        return
      }

      setUsernameStatus({ checking: true, available: null, message: '' })
      
      try {
        const result = await checkUsernameAvailability(formData.username)
        setUsernameStatus({
          checking: false,
          available: result.available,
          message: result.available ? 'Username is available!' : 'Username is already taken'
        })
      } catch (error) {
        setUsernameStatus({
          checking: false,
          available: false,
          message: 'Error checking username availability'
        })
      }
    }

    const timeoutId = setTimeout(checkUsername, 500) // Debounce for 500ms
    return () => clearTimeout(timeoutId)
  }, [formData.username, checkUsernameAvailability])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Client-side validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters long')
      return
    }

    if (!usernameStatus.available) {
      setError('Please choose a different username')
      return
    }

    setLoading(true)

    const result = await signUp(formData.email, formData.password, formData.username)

    if (result.success) {
      // Redirect to homepage on successful registration
      if (onBackToGame) {
        onBackToGame()
      }
    } else {
      setError(result.error)
    }
    setLoading(false)
  }

  const getUsernameStatusStyle = () => {
    if (usernameStatus.checking) {
      return { color: '#666', fontSize: '14px' }
    }
    if (usernameStatus.available === true) {
      return { color: '#4caf50', fontSize: '14px' }
    }
    if (usernameStatus.available === false) {
      return { color: '#f44336', fontSize: '14px' }
    }
    return { color: '#666', fontSize: '14px' }
  }

  return (
    <div style={{ maxWidth: '400px', margin: '0 auto', padding: '20px' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Sign Up</h2>
      
      {error && (
        <div style={{ 
          background: '#ffebee', 
          color: '#c62828', 
          padding: '10px', 
          borderRadius: '4px', 
          marginBottom: '15px' 
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Username:</label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            minLength="3"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px'
            }}
          />
          {formData.username.length > 0 && (
            <div style={{ marginTop: '5px', ...getUsernameStatusStyle() }}>
              {usernameStatus.checking ? 'Checking username...' : usernameStatus.message}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Email:</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px'
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Password:</label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            minLength="6"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Confirm Password:</label>
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px'
            }}
          />
        </div>

        <button
          type="submit"
          disabled={loading || usernameStatus.checking || !usernameStatus.available}
          style={{
            width: '100%',
            padding: '12px',
            background: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            cursor: (loading || usernameStatus.checking || !usernameStatus.available) ? 'not-allowed' : 'pointer',
            opacity: (loading || usernameStatus.checking || !usernameStatus.available) ? 0.7 : 1
          }}
        >
          {loading ? 'Creating account...' : 'Sign Up'}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: '15px' }}>
        <button
          onClick={onSwitchToLogin}
          style={{
            background: 'none',
            border: 'none',
            color: '#1976d2',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Already have an account? Login
        </button>
      </div>
    </div>
  )
}

export default SupabaseRegisterForm 